import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Reveal, Overline } from "@/components/Reveal";

export default function Membership() {
    const [tiers, setTiers] = useState([]);
    const [busy, setBusy] = useState(null);
    const { user } = useAuth();
    const navigate = useNavigate();

    useEffect(() => { api.get("/membership-tiers").then((r) => setTiers(r.data.sort((a, b) => a.price - b.price))); }, []);

    const subscribe = async (tier) => {
        if (!user) { toast.info("Please sign in to become a member."); navigate("/login"); return; }
        setBusy(tier.id);
        try {
            const { data } = await api.post("/checkout/membership", { tier_id: tier.id, user_id: user.id, origin_url: window.location.origin });
            window.location.href = data.checkout_url;
        } catch { toast.error("Could not start subscription."); setBusy(null); }
    };

    return (
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
            <Reveal>
                <div className="text-center">
                    <Overline>The Inner Circle</Overline>
                    <h1 className="mt-3 font-serif text-5xl sm:text-6xl text-cream">Membership</h1>
                    <p className="mt-4 text-ash max-w-2xl mx-auto">Walk under the house's cover month to month, member-only spells, early access, private guidance, and discounts on everything at Sacred Earthworks.</p>
                </div>
            </Reveal>

            <div className="mt-14 grid md:grid-cols-3 gap-6 items-stretch">
                {tiers.map((t, i) => {
                    const isCurrent = user && user.membership_status === "active" && user.membership_tier === t.name;
                    return (
                    <Reveal key={t.id} delay={i * 0.08}>
                        <div data-testid={`tier-${t.id}`} className={`h-full flex flex-col p-8 rounded-2xl border transition-colors ${t.featured ? "bg-surface2 border-gold glow-gold" : "bg-surface border-gold/15"}`}>
                            {isCurrent ? <span className="self-start text-[10px] uppercase tracking-widest bg-moss text-cream px-3 py-1 rounded-full mb-4">Your Plan</span>
                                : t.featured ? <span className="self-start text-[10px] uppercase tracking-widest bg-gold text-ink px-3 py-1 rounded-full mb-4">Most Chosen</span> : null}
                            <h3 className="font-serif text-3xl text-cream">{t.name}</h3>
                            <div className="mt-3 flex items-baseline gap-1"><span className="text-4xl text-gold">${t.price.toFixed(0)}</span><span className="text-faded">/{t.interval}</span></div>
                            <ul className="mt-6 space-y-3 flex-1">
                                {t.perks.map((p, j) => (
                                    <li key={j} className="flex gap-3 text-sm text-ash"><Check className="w-4 h-4 text-gold shrink-0 mt-0.5" />{p}</li>
                                ))}
                            </ul>
                            <button disabled={busy === t.id || isCurrent} onClick={() => subscribe(t)} data-testid={`subscribe-${t.id}`}
                                className={`mt-8 w-full rounded-full py-3 font-medium transition-colors active:scale-95 disabled:opacity-60 ${t.featured ? "bg-gold text-ink hover:bg-goldbright" : "border border-gold/30 text-gold hover:bg-gold/10"}`}>
                                {isCurrent ? "Current Plan" : busy === t.id ? "Redirecting…" : (user && user.membership_status === "active" ? "Switch to this" : "Become a Member")}
                            </button>
                        </div>
                    </Reveal>
                    );
                })}
            </div>
            <p className="mt-10 text-center text-xs text-faded">Billed monthly via Stripe. Cancel anytime from your account.</p>
        </div>
    );
}
